(ns ui.wallet.page
  "The credits wallet, modelled on the OpenRouter credits page: the balance,
   the Auto Top-Up panel (`ui.wallet.plan`), the recent transactions
   (`ui.wallet.transactions`), the payment method and the rates
   (`ui.wallet.rates`).

   Launched: it owns /ui/subscription and the :pages/subscription page id,
   so every existing link (navbar, credits-exhausted modal, checkout
   callback, help center) lands here. The old calculator page stays in the
   codebase unregistered; rolling back is re-registering it in ui.register
   and reverting this page id and route.

   All texts are `[:i18n ...]` references that `render` resolves once for the
   browser's language (see `ui.wallet.i18n`)."
  (:require [app.client-log :as client-log]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [ui.app-frame :as app-frame]
            [ui.elements]
            [storrito.wallet :as wallet]
            [ui.subscription.core :as core]
            [ui.wallet.billing-email :as billing-email]
            [ui.wallet.i18n :as i18n]
            [ui.wallet.on-demand :as on-demand]
            [ui.wallet.plan :as plan]
            [ui.wallet.rates :as rates]
            [ui.wallet.top-up :as top-up]
            [ui.wallet.transactions :as transactions]))

(defn balance-note
  "The nudge once the balance is used up. A nil balance means the
   organization never received credits. The welcome gift is not mentioned
   here: the funnel before this page already did, and by the time the page
   is seen the daily fees have usually started spending it."
  [session]
  (let [balance (:credit-balance session)]
    (when (and balance
               (not (pos? balance)))
      [:i18n :balance/used-up])))

(defn balance-panel
  [w]
  (let [session (query/get-result w core/user-session-query)]
    [:div {:class ["rounded-lg" "bg-gray-50" "p-6" "ring-1" "ring-inset" "ring-gray-200"]}
     [:div.text-xs.font-semibold.uppercase.tracking-wide.text-gray-500
      [:i18n :balance/total-available]]
     [:div.mt-2.text-4xl.font-bold.text-gray-900
      (or (:credit-balance-formatted session)
          "$0.00")]
     [:p.mt-1.text-sm.text-gray-500 [:i18n :balance/pay-as-you-go]]
     (when-let [note (balance-note session)]
       [:p.mt-3.text-sm.font-medium.text-gray-800 note])
     [:div.mt-4
      [:ui/a {:ui/location {:location/page-id :pages/subscription-statement
                            :location/params {}}
              :class ["text-sm" "font-medium" "text-blue-600" "hover:underline"]}
       [:i18n :balance/view-usage]
       [:i.fas.fa-external-link-alt.ml-1.text-xs]]]]))

(defn payment-method
  [details]
  (case (:type details)
    "card" (let [card (:card details)
                 type->class {"visa" "fa-cc-visa"
                              "master" "fa-cc-mastercard"
                              "american_express" "fa-cc-amex"
                              "discover" "fa-cc-discover"
                              "jcb" "fa-cc-jcb"
                              "diners_club" "fa-cc-diners-club"
                              "unionpay" "fa-cc-master"
                              nil "fa-credit-card"}]
             [:span
              [:i {:class (cons (type->class (:type card))
                                ["fab" "fa-xl" "w-7" "text-gray-500"])}]
              [:i18n :payment/card {:last4 (:last4 card)}]
              [:span.ml-2
               [:i.fas.fa-clock.text-gray-500.w-4]
               (:expiry_month card) "/" (:expiry_year card)]])
    "paypal" [:span [:i.fa-brands.fa-cc-paypal.fa-2xl.w-10.text-gray-500]
              "Paypal"]
    "alipay" [:span [:i.fa-brands.fa-alipay.fa-2xl.w-10.text-gray-500]
              "Alipay"]
    "apple_pay" [:span [:i.fa-brands.fa-cc-apple-pay.fa-2xl.w-10.text-gray-500]
                 "Apple Pay"]
    "google_pay" [:span [:i.fa-brands.fa-google-pay.fa-2xl.w-10.text-gray-500]
                  "Google Pay"]
    "wire_transfer" [:span [:i18n :payment/wire-transfer]]
    [:span (:type details)]))

(defn payment-panel
  [w]
  (let [subscription (query/get-result w core/subscription-query)
        paddle-subscription (:organization/paddle-subscription subscription)]
    (when (#{"active" "past_due"} (:status paddle-subscription))
      [:div {:class ui.elements/panel}
       [:h2 {:class ui.elements/h3} [:i18n :payment/title]]
       [:dl.mt-3.grid.grid-cols-2.gap-4.text-sm.text-gray-700
        [:div
         [:dt.font-medium.text-gray-500 [:i18n :payment/method]]
         [:dd.mt-1 (payment-method (:subscription/payment-method-details subscription))]]
        [:div
         [:dt.font-medium.text-gray-500 [:i18n :payment/billing-email]]
         [:dd.mt-1 (billing-email/billing-email w (:email (:customer subscription)))]]
        [:div
         [:dt.font-medium.text-gray-500 [:i18n :payment/reference]]
         [:dd.mt-1.text-gray-500 (:id paddle-subscription)]]]
       [:a {:href "/subscription/invoices"
            :target "_blank"
            :class ["mt-4" "inline-block" "text-sm" "text-blue-600" "hover:underline"]}
        [:i18n :payment/update]]])))

(defn campaign-offer-panel
  "The eligible campaign (usually the welcome discount): the offer the
   emails and the navbar banner promise, visible on the page their links
   land on. The discount itself is claimed automatically before the
   checkout (see `ui.wallet.top-up/claim-campaign-command`), so the card
   only informs — there is nothing to click."
  [w]
  (when-let [campaign (query/get-result w core/campaign-query)]
    [:div {:class ["rounded-lg" "bg-gradient-to-r" "from-green-50" "to-emerald-50"
                   "p-6" "ring-1" "ring-inset" "ring-green-200"]}
     [:div.flex.items-start.gap-3
      [:svg {:class ["w-6" "h-6" "text-green-600" "flex-shrink-0" "mt-0.5"]
             :fill "none"
             :stroke "currentColor"
             :viewBox "0 0 24 24"}
       [:path {:stroke-linecap "round"
               :stroke-linejoin "round"
               :stroke-width "2"
               :d "M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"}]]
      [:div
       [:div.flex.items-center.gap-2
        [:span.font-semibold.text-gray-900 (:campaign/name campaign)]
        [:span.inline-flex.items-center.rounded-md.bg-green-50.px-2.py-1.text-xs.font-medium.text-green-700.ring-1.ring-inset.ring-green-600
         [:i18n :rates/off {:percent (int (* 100 (:campaign/discount campaign)))}]]]
       [:div.text-sm.text-gray-700.mt-1
        [:i18n (i18n/service-key (:campaign/feature campaign) :name)]]
       [:div.text-xs.text-gray-600.mt-1
        [:i18n :offer/applied-automatically]]]]]))

(defn loading?
  [w]
  (or (query/loading? w core/subscription-query)
      (command/issued? w core/import-paddle-events-command)))

(defn checkout-customer?
  "True for an organization without a subscription: the page's job for it
   is the first checkout (`ui.wallet.on-demand/checkout-panel`)."
  [w]
  (let [session (query/get-result w core/user-session-query)
        paddle-subscription (:organization/paddle-subscription
                              (query/get-result w core/subscription-query))]
    (and (not (#{"active" "past_due"} (:status paddle-subscription)))
         (not (:subscription-id session)))))

(defn top-up-panels
  "Every subscriber gets the same `ui.wallet.on-demand` panels; a monthly
   or yearly subscription additionally shows the read-only plan strip
   above them. Customers without a subscription get the checkout, and a
   session with a subscription Paddle did not answer for gets neither: a
   checkout would create a second subscription."
  [w]
  (let [session (query/get-result w core/user-session-query)
        paddle-subscription (:organization/paddle-subscription
                              (query/get-result w core/subscription-query))]
    (cond
      (#{"active" "past_due"} (:status paddle-subscription))
      (concat
        (when-not (wallet/on-demand? paddle-subscription)
          [(plan/plan-panel w)])
        (on-demand/panels w))

      (:subscription-id session)
      (list (plan/paddle-unavailable))

      :else
      (list (on-demand/checkout-panel w)))))

(defn panel-order
  "The panels top to bottom. A customer without a subscription sees the
   checkout first: 23 of the 30 trial organizations that opened this page
   on 23./24.09.2026 (after the click events of #4837 went live) never
   touched an amount chip or the button, and they had to scroll past the
   balance and the welcome-offer card to reach them. The offer card is
   left out for them - the checkout panel states the same discount in
   `ui.wallet.on-demand/campaign-note`. Subscribers keep the balance on
   top: their reason to come here is the balance and the transactions."
  [checkout-customer?]
  (if checkout-customer?
    [:top-up :balance :transactions :payment :rates]
    [:balance :campaign-offer :top-up :transactions :payment :rates]))

(defn content
  [w]
  (if (loading? w)
    [:span [:i18n :page/loading]]
    (let [panels {:balance balance-panel
                  :campaign-offer campaign-offer-panel
                  :top-up top-up-panels
                  :transactions transactions/panel
                  :payment payment-panel
                  :rates (fn [_] [:div.text-center (rates/link)])}]
      (into [:div {:class ["max-w-2xl" "space-y-6"]}]
            (map (fn [k] ((panels k) w)))
            (panel-order (checkout-customer? w))))))

(defn render
  [w]
  (app-frame/app-frame
    w
    (i18n/translate w (content w))))

(defn amount-from-location
  "The amount a link preselects (`/ui/subscription?amount=5`), or nil.
   Only the preset chips count, so a link cannot preselect an arbitrary
   or too small charge. The trial banner links with $5: it is the best
   converting entry (44 shown → 10 checkouts, week of 16.–21.09.2026),
   while 77 % of the trial users who reach this page never touch an
   amount chip - the link now makes the choice for them."
  [location]
  (let [raw (get-in location [:location/query-params :amount])
        n (when (string? raw) (js/parseInt raw 10))]
    (when (contains? (set top-up/presets) n)
      n)))

(defn amount-actions
  "Preselects the linked amount, or clears a stale choice."
  [location]
  (if-let [amount (amount-from-location location)]
    [[:store/assoc :wallet/amount amount]]
    [[:store/dissoc :wallet/amount]]))

(def page
  {:page-id :pages/subscription
   :route [["ui" "subscription"]]
   :on-load (fn [location]
              ;; Funnel step, unchanged from the old page so the series
              ;; stays comparable across the launch: how many users reach
              ;; the subscription page and from where (the onboarding
              ;; subscribe screen links here with ?source=subscribe-screen).
              (client-log/log! :subscription/page-viewed
                               {:subscription/source
                                (or (get-in location
                                            [:location/query-params :source])
                                    "direct")})
              (-> core/query-actions
                  (into (transactions/load-actions))
                  (into (amount-actions location))
                  (conj [:subscription/redirect-if-legacy]
                        [:subscription/watch-exit])))
   :render #'render})
